import type { FramingAvailability } from "../framingInspector";

/**
 * Operator-facing meaning for every allowlisted availability state
 * (CROP-WB-012). Each entry answers: what is known, why the pixels are not
 * shown, and what the operator can do next. Unknown future codes fall back
 * to an honest generic state instead of crashing or inventing evidence.
 */

export type FramingEvidenceAction =
  | "none"
  | "retry"
  | "refresh_once"
  | "reauthenticate"
  | "open_engineering_details";

export type FramingEvidenceStateView = {
  title: string;
  explanation: string;
  action: FramingEvidenceAction;
  tone: "neutral" | "info" | "warning" | "destructive";
  /** Whether metadata/geometry may still be shown for this stage. */
  metadataVisible: boolean;
};

const STATES: Record<FramingAvailability, FramingEvidenceStateView> = {
  available: {
    title: "Available",
    explanation: "Evidence pixels are available.",
    action: "none",
    tone: "neutral",
    metadataVisible: true,
  },
  not_applicable: {
    title: "Not applicable",
    explanation:
      "This stage does not exist for this scene; the pipeline never produces it here.",
    action: "none",
    tone: "neutral",
    metadataVisible: true,
  },
  not_recorded: {
    title: "Not recorded",
    explanation:
      "Framing telemetry for this stage was never recorded at this revision or schema.",
    action: "open_engineering_details",
    tone: "info",
    metadataVisible: true,
  },
  ephemeral: {
    title: "Never retained",
    explanation:
      "These provider pixels were processed transiently and deliberately not stored. Digest and dimensions are shown when recorded.",
    action: "none",
    tone: "info",
    metadataVisible: true,
  },
  external: {
    title: "External to our storage",
    explanation:
      "The provider held these pixels; only recorded identity metadata exists.",
    action: "none",
    tone: "info",
    metadataVisible: true,
  },
  expired: {
    title: "Signed link expired",
    explanation: "The short-lived media link expired.",
    action: "refresh_once",
    tone: "warning",
    metadataVisible: true,
  },
  deleted: {
    title: "Deleted",
    explanation:
      "This evidence was removed by retention policy or erasure; it cannot be re-signed.",
    action: "none",
    tone: "warning",
    metadataVisible: true,
  },
  missing: {
    title: "Missing",
    explanation: "The recorded artifact could not be found in storage.",
    action: "open_engineering_details",
    tone: "warning",
    metadataVisible: true,
  },
  hash_mismatch: {
    title: "Integrity mismatch",
    explanation:
      "The stored bytes do not match the recorded digest; the artifact is not shown as canonical.",
    action: "open_engineering_details",
    tone: "destructive",
    metadataVisible: true,
  },
  correlation_mismatch: {
    title: "Correlation mismatch",
    explanation:
      "The requested attempt does not belong to this scene or Echo; no other attempt was substituted.",
    action: "none",
    tone: "destructive",
    metadataVisible: false,
  },
  dimension_mismatch: {
    title: "Dimension mismatch",
    explanation:
      "Recorded geometry does not match the media dimensions, so overlays are not projected onto it.",
    action: "open_engineering_details",
    tone: "warning",
    metadataVisible: true,
  },
  signing_failed: {
    title: "Signing failed",
    explanation:
      "The media link could not be created. Metadata and geometry stay visible.",
    action: "retry",
    tone: "warning",
    metadataVisible: true,
  },
  decode_unsupported: {
    title: "Cannot decode in this browser",
    explanation:
      "The media format cannot be decoded here; the retained contact sheet is shown when available.",
    action: "none",
    tone: "info",
    metadataVisible: true,
  },
  network_error: {
    title: "Network problem",
    explanation:
      "The media could not be fetched. Loaded evidence is kept; this is not deletion or expiry.",
    action: "retry",
    tone: "warning",
    metadataVisible: true,
  },
  projection_only: {
    title: "Geometry only",
    explanation:
      "Geometry was recorded for this stage but the pixels are not viewable.",
    action: "none",
    tone: "info",
    metadataVisible: true,
  },
  legacy_mutability_unknown: {
    title: "Legacy evidence",
    explanation:
      "This artifact predates immutable attempt storage. It is shown, but it cannot prove exact historical attempt identity.",
    action: "none",
    tone: "info",
    metadataVisible: true,
  },
  unknown: {
    title: "Unknown state",
    explanation:
      "The server reported an availability state this client does not know. The raw safe code is in Engineering details.",
    action: "open_engineering_details",
    tone: "info",
    metadataVisible: true,
  },
};

export function framingEvidenceState(
  availability: FramingAvailability,
): FramingEvidenceStateView {
  return STATES[availability] ?? STATES.unknown;
}

export const ALL_FRAMING_EVIDENCE_STATES = STATES;
